import {
  exerciseAttribute,
  freeAttribute,
  handedOverAttribute,
  passAttribute,
  recordAttribute,
  stageAttribute,
} from "../content/lianxi/exercise-fields.js";
import type { DrillProgress } from "../handwriting/xiezi-stages.js";
import { LianxiRecorder } from "./lianxi-recorder.js";
import { blockHandoverCount } from "./lianxi-handover.js";
import { said } from "./xiezi-parts.js";

/**
 * What an Exercise block says to the script, beyond its characters: the row
 * of items over the square, each character's kept place, and where a press
 * posts.
 *
 * `partials/lianxi/drill.html` draws one item per character, and
 * `src/user/lianxi-drill-markup.ts` puts each character's kept stage and pass
 * on its item and names where to post. `lianxi-start.ts` reads all of it
 * through this.
 */

/** The row over the square, one item per character. */
const itemSelector = "[data-cb-xiezi-character]";

/** The row's items, by the character each is for. */
export function characterItems(
  block: HTMLElement,
): (character: string) => HTMLElement | undefined {
  const items = [...block.querySelectorAll<HTMLElement>(itemSelector)];

  return (character) =>
    items.find((item) => item.dataset["cbXieziCharacter"] === character);
}

/** A whole number from an attribute, or nought for anything else. */
function counted(value: string | undefined): number {
  const number = Number(value ?? 0);

  return Number.isInteger(number) && number >= 0 ? number : 0;
}

/** Where an item says its character stands, or the beginning. */
export function keptPlace(item: HTMLElement | undefined): DrillProgress {
  return {
    stage: counted(item?.dataset[stageAttribute]),
    pass: counted(item?.dataset[passAttribute]),
    stroke: 0,
  };
}

/**
 * The recorder for a block that names where to post, or nothing for the
 * public block, which keeps no place.
 * @param block - The block.
 * @param characters - The characters the Exercise was built over, in order.
 * @param fetching - How to post.
 * @returns The recorder, or nothing.
 */
export function recorderOf(
  block: HTMLElement,
  characters: readonly string[],
  fetching: typeof fetch,
): LianxiRecorder | undefined {
  const address = said(block, recordAttribute);
  const slug = said(block, exerciseAttribute);

  return address === "" || slug === ""
    ? undefined
    : new LianxiRecorder({
        address,
        slug,
        characters,
        fetch: fetching,
        handedOver: block.dataset[handedOverAttribute] !== undefined,
        handoverCount: blockHandoverCount(block),
        free: block.dataset[freeAttribute] !== undefined,
      });
}
